/**
 * useChat.tsx
 *
 * File-level:
 * - React hook to manage SSE connection to /api/chat/connect and provide helpers:
 *   - messages: incoming message list
 *   - unreadCount: unread count for current user
 *   - sendMessage(toUserId, message): send message via POST /api/chat/send
 *   - markRead(withUserId): mark messages read via POST /api/chat/mark-read
 *
 * Notes:
 * - EventSource does not support custom headers; we pass token via query param ?token=<token>.
 * - Token is expected to be the logged-in user's id (compatibility). Replace with secure tokens/JWT in production.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { apiBase } from '@/utils/api';

export type ChatMessage = {
  id?: number | string;
  fromUserId: string;
  toUserId: string;
  message: string;
  isRead?: boolean;
  createdAt?: string;
};

type UseChatReturn = {
  messages: ChatMessage[];
  unreadCount: number;
  /** Number of distinct other users who currently have unread messages for me */
  unreadSendersCount: number;
  sendMessage: (toUserId: string, message: string) => Promise<{ success: boolean; id?: any; error?: any }>;
  markRead: (withUserId: string) => Promise<{ success: boolean; unreadCount?: number }>;
  connected: boolean;
  disconnect: () => void;
};

/**
 * Resolve Chat API base from runtime apiBase().
 * We append "/chat" to the base returned by apiBase() which already normalizes trailing slashes.
 */
const CHAT_API_BASE = (() => {
  try {
    const base = apiBase();
    return `${base.replace(/\/+$/g, '')}/chat`;
  } catch {
    // Fallback to relative path if apiBase fails for some reason
    return '/api/chat';
  }
})();

/**
 * useChat
 *
 * @description Hook to manage EventSource SSE connection and chat actions
 * @param token optional token (if not provided, hook will take userId from localStorage.userData.userId)
 * @returns UseChatReturn
 */
export default function useChat(token?: string, enabled = true): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  /** Number of distinct other users who currently have unread messages for me */
  const [unreadSendersCount, setUnreadSendersCount] = useState<number>(0);
  const [connected, setConnected] = useState<boolean>(false);
  const esRef = useRef<EventSource | null>(null);

  const getToken = useCallback(() => {
    if (token) return token;
    try {
      const stored = localStorage.getItem('userData');
      if (!stored) return null;
      const user = JSON.parse(stored);
      return user?.userId ?? null;
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    // If hook is disabled we should not establish a connection.
    // Also ensure an existing connection is closed when disabled toggles to false.
    if (!enabled) {
      try {
        if (esRef.current) {
          esRef.current.close();
        }
      } catch {}
      esRef.current = null;
      setConnected(false);
      return;
    }

    const t = getToken();
    if (!t) {
      // not logged in
      return;
    }

    const url = `${CHAT_API_BASE}/connect?token=${encodeURIComponent(t)}`;

    let es: EventSource | null = null;
    try {
      es = new EventSource(url);
      esRef.current = es;
    } catch (err) {
      // EventSource construction failed (invalid URL or network issues)
      // Ensure we don't throw and keep hook usable for fetch-based operations.
      // eslint-disable-next-line no-console
      console.warn('[useChat] EventSource creation failed for', url, err);
      esRef.current = null;
      setConnected(false);
      return;
    }

    es.addEventListener('open', () => {
      setConnected(true);
    });

    es.addEventListener('connected', (ev: any) => {
      // server sends connected event initially
      try {
        // console.log('SSE connected:', JSON.parse(ev.data));
      } catch {}
    });

    es.addEventListener('message', (ev: any) => {
      try {
        const data = JSON.parse(ev.data);
        // generic message event used by server for new incoming messages
        if (data && data.message !== undefined && data.fromUserId) {
          setMessages((prev) => [...prev, data]);
        }
      } catch (e) {
        // ignore parse errors
      }
    });

    es.addEventListener('message_sent', (ev: any) => {
      try {
        const data = JSON.parse(ev.data);
        // Optional: add sent message to local list
        if (data && data.toUserId && data.message) {
          setMessages((prev) => [...prev, { fromUserId: getToken() || 'me', toUserId: data.toUserId, message: data.message, createdAt: data.createdAt }]);
        }
      } catch {}
    });

    es.addEventListener('unread', (ev: any) => {
      try {
        const data = JSON.parse(ev.data);
        setUnreadCount(Number(data.count ?? 0));
      } catch (e) {
        // ignore
      }
    });

    // Listen for server-sent aggregated distinct-senders count
    es.addEventListener('unread_senders', (ev: any) => {
      try {
        const data = JSON.parse(ev.data);
        const cnt = Number(data.count ?? 0);
        setUnreadSendersCount(Number.isFinite(cnt) ? cnt : 0);

        // Also dispatch a global custom event so other parts of the app (e.g., Dashboard)
        // can react without needing to open another SSE connection.
        try {
          window.dispatchEvent(new CustomEvent('unread-senders-changed', { detail: { count: cnt } }));
        } catch (dispatchErr) {
          // ignore dispatch errors
        }
      } catch (e) {
        // ignore parse errors
      }
    });

    es.addEventListener('error', (err) => {
      // Attempt reconnection is handled by browser for EventSource; update state accordingly
      setConnected(false);
    });

    // Clean up on unmount or when `enabled` changes
    return () => {
      try {
        es && es.close();
      } catch {}
      esRef.current = null;
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken, enabled]);

  /**
   * sendMessage
   *
   * @param toUserId recipient id
   * @param message message text
   */
  const sendMessage = useCallback(async (toUserId: string, message: string) => {
    try {
      const stored = localStorage.getItem('userData');
      if (!stored) return { success: false, error: 'Not logged in' };
      const user = JSON.parse(stored);
      const fromUserId = user.userId;
      if (!fromUserId) return { success: false, error: 'Missing fromUserId' };

      const resp = await fetch(`${CHAT_API_BASE}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromUserId, toUserId, message })
      });
      const json = await resp.json();
      if (json && json.success) {
        // Optionally append to messages locally (server also sends message_sent)
        setMessages((prev) => [...prev, { id: json.id, fromUserId, toUserId, message, isRead: false, createdAt: json.createdAt }]);
        return { success: true, id: json.id };
      }
      return { success: false, error: json?.message || 'Failed to send' };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }, []);

  /**
   * markRead
   *
   * @param withUserId mark messages from withUserId -> current user as read
   */
  const markRead = useCallback(async (withUserId: string) => {
    try {
      const stored = localStorage.getItem('userData');
      if (!stored) return { success: false };
      const user = JSON.parse(stored);
      const userId = user.userId;
      if (!userId) return { success: false };

      const resp = await fetch(`${CHAT_API_BASE}/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, withUserId })
      });
      const json = await resp.json();
      if (json && json.success) {
        setUnreadCount(Number(json.unread ?? json.unreadCount ?? 0));
        return { success: true, unreadCount: json.unread ?? json.unreadCount };
      }
      return { success: false };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }, []);

  const disconnect = useCallback(() => {
    if (esRef.current) {
      try {
        esRef.current.close();
      } catch {}
      esRef.current = null;
      setConnected(false);
    }
  }, []);

  return {
    messages,
    unreadCount,
    unreadSendersCount,
    sendMessage,
    markRead,
    connected,
    disconnect
  };
}